import { useState } from 'react';
import { Alert, Box, Button, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { PrimaryButton } from '@/components/PrimaryButton';
import { getQuotationStatusLabel } from '@/features/operations/quotationStatus';
import { receiveQuotationDevice } from '@/features/operations/api';
import { ConditionConfirmDialog } from '@/features/operations/components/ConditionConfirmDialog';
import { ConditionFields } from '@/features/operations/components/ConditionFields';
import { colorAfterModelChange } from '@/features/operations/catalogue';
import {
  CatalogueModelFields,
  ColorField,
} from '@/features/operations/components/CatalogueModelFields';
import {
  buildDeclaredConditionDraft,
  conditionAnswers,
  conditionDifferences,
  conditionQuestions,
  declaredBatteryInput,
  type ConditionDifference,
  type ConditionDraft,
} from '@/features/operations/condition';
import { DeviceSheet } from '@/features/operations/components/DeviceSheet';
import { priceError, PRICE_CURRENCY, toPriceInput } from '@/features/operations/money';
import type {
  BranchOption,
  DeviceDetail,
  DeviceModelOption,
  QuotationRequestItem,
  ReceiveDeviceInput,
  ReceptionDecision,
  CommercialModality,
} from '@/features/operations/types';

/// A Quoted request is received; so is a Rejected one, which is the renegotiation path and can
/// only end in approving it (#92, BR-025). The backend enforces the same rule; this only avoids
/// offering a form that would be refused.
const receivableQuotationStatuses = ['QUOTED', 'REJECTED'];

/// The estimate is where the adjustment starts from (ADR 0005 c.4: "USD 300 estimated becomes
/// USD 270"). Only when it is in USD: prices are stored in USD (BR-018), and converting an
/// estimate in another currency here would be guessing an exchange rate.
function initialPaidPrice(quotation: QuotationRequestItem): string {
  return quotation.estimate?.currency === PRICE_CURRENCY
    ? toPriceInput(quotation.estimate.amountMinor)
    : '';
}

/**
 * Where the form starts. A request rejected at the counter starts from what was observed then
 * (BR-025): the phone was already inspected, and approving it after a renegotiation should not
 * mean doing it again. Any other request starts from what was declared (ADR 0005 c.1, #182).
 */
function startingPoint(quotation: QuotationRequestItem) {
  const saved = quotation.rejectedReception;

  if (saved) {
    const condition: ConditionDraft = {};

    conditionQuestions(quotation.inspectionFields).forEach((field) => {
      condition[field.fieldId] =
        saved.answers.find((answer) => answer.fieldId === field.fieldId)?.optionId ?? '';
    });

    return {
      phoneModelId: saved.phoneModelId,
      branchId: saved.branchId ?? '',
      storageGb: String(saved.storageGb),
      serialNumber: saved.serialNumber ?? '',
      colorId: saved.colorId ?? '',
      batteryHealth: saved.batteryHealth === null ? '' : String(saved.batteryHealth),
      paidPrice: toPriceInput(saved.adjustedValueMinor),
      commercialModality: (quotation.commercialModality ?? '') as CommercialModality | '',
      condition,
    };
  }

  return {
    phoneModelId: quotation.phoneModelId ?? '',
    // Required, and with nothing to default to: the request says what the phone is, never
    // where it ended up. Whoever has it in hand is the one who knows (WF-02 step 2).
    branchId: '',
    storageGb: quotation.storageGb === null ? '' : String(quotation.storageGb),
    serialNumber: '',
    colorId: '',
    batteryHealth: declaredBatteryInput(quotation.answers),
    paidPrice: initialPaidPrice(quotation),
    commercialModality: '' as CommercialModality | '',
    condition: buildDeclaredConditionDraft(quotation.inspectionFields, quotation.answers),
  };
}

/**
 * Reception, inside the detail of a request: the phone declared here is now on the counter.
 *
 * This is the only place a device is created, and it records the phone in **one** pass:
 * what it is -- model, capacity, serial, colour, battery -- and what state it was found in
 * (#89). The person is standing there with the phone once, so the form is one form and the
 * save is one write.
 *
 * The form opens with what was declared -- model, capacity and the three characteristics -- and
 * the employee corrects only what differs (ADR 0005 c.1, #182). What only the phone can tell is
 * added here: the serial number, the colour, picked from the colours of the model (#186), and
 * the branch. Whatever was corrected is still named against the declaration before saving.
 *
 * The reception closes with an explicit decision (ADR 0005 point c, #92): approving creates the
 * device, rejecting keeps what was observed on the request so it can be renegotiated and
 * approved later from here, without inspecting the phone again (BR-025).
 *
 * Once received, the same place shows the device's internal sheet (#88).
 */
export function ReceptionPanel({
  token,
  quotation,
  modelOptions,
  branchOptions,
  onReceived,
  onDeviceUpdated,
}: {
  token: string;
  quotation: QuotationRequestItem;
  modelOptions: DeviceModelOption[];
  branchOptions: BranchOption[];
  onReceived: (quotation: QuotationRequestItem) => void;
  onDeviceUpdated: (device: DeviceDetail) => void;
}) {
  /// Receiving is something somebody does when the phone is on the counter, so it is asked for
  /// rather than offered: until then the request only says that no device arrived.
  const [isReceiving, setIsReceiving] = useState(false);
  /// Everything starts as declared, or as observed at a rejected reception: the employee only
  /// changes what the phone on the counter contradicts.
  const [start] = useState(() => startingPoint(quotation));
  const [phoneModelId, setPhoneModelId] = useState(start.phoneModelId);
  const [branchId, setBranchId] = useState(start.branchId);
  const [storageGb, setStorageGb] = useState(start.storageGb);
  const [serialNumber, setSerialNumber] = useState(start.serialNumber);
  const [colorId, setColorId] = useState(start.colorId);
  const [batteryHealth, setBatteryHealth] = useState(start.batteryHealth);
  const [paidPrice, setPaidPrice] = useState(start.paidPrice);
  const [commercialModality, setCommercialModality] = useState(start.commercialModality);
  const [condition, setCondition] = useState<ConditionDraft>(start.condition);
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [pendingDifferences, setPendingDifferences] = useState<ConditionDifference[] | null>(null);
  /// Which button opened the confirmation, so confirming carries out that decision.
  const [pendingDecision, setPendingDecision] = useState<ReceptionDecision>('APPROVE');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /// A closed branch is not somewhere a phone can be received into, so it is not offered.
  const activeBranches = branchOptions.filter((option) => option.isActive);

  if (quotation.device) {
    return (
      <Box>
        <Typography variant="overline" color="text.secondary">
          RECEPCIÓN DEL EQUIPO
        </Typography>
        <DeviceSheet
          token={token}
          device={quotation.device}
          quotation={quotation}
          modelOptions={modelOptions}
          branchOptions={branchOptions}
          onUpdated={onDeviceUpdated}
        />
      </Box>
    );
  }

  if (!receivableQuotationStatuses.includes(quotation.status)) {
    return (
      <Box>
        <Typography variant="overline" color="text.secondary">
          RECEPCIÓN DEL EQUIPO
        </Typography>
        <Typography variant="body2" color="text.secondary">
          La cotización está en estado {getQuotationStatusLabel(quotation.status)}, así que no se
          puede recibir el equipo.
        </Typography>
      </Box>
    );
  }

  const isRejected = quotation.status === 'REJECTED';

  /// Back to where the form started: a form opened by mistake leaves nothing typed behind it.
  const resetForm = () => {
    const next = startingPoint(quotation);
    setPhoneModelId(next.phoneModelId);
    setBranchId(next.branchId);
    setStorageGb(next.storageGb);
    setSerialNumber(next.serialNumber);
    setColorId(next.colorId);
    setBatteryHealth(next.batteryHealth);
    setPaidPrice(next.paidPrice);
    setCommercialModality(next.commercialModality);
    setCondition(next.condition);
    setInspectionNotes('');
    setError(null);
    setIsReceiving(false);
  };

  if (!isReceiving) {
    return (
      <Box>
        <Typography variant="overline" color="text.secondary">
          RECEPCIÓN DEL EQUIPO
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {isRejected
            ? 'La cotización se rechazó en la recepción y lo observado quedó guardado. Si se renegocia, retomá la recepción para aprobarla: crea el equipo en Pendiente de publicación.'
            : 'Todavía no se recibió el equipo. Cuando la persona lo traiga al local, registrá la recepción y cerrala aprobando o rechazando la cotización.'}
        </Typography>
        {activeBranches.length ? (
          <PrimaryButton onClick={() => setIsReceiving(true)}>
            {isRejected ? 'Retomar recepción' : 'Registrar recepción'}
          </PrimaryButton>
        ) : (
          // Every received phone names where it is standing, so with no branch on file there
          // is nothing to offer. Saying what is missing beats a select with nothing in it.
          <Alert severity="warning">
            No hay sucursales activas, así que no se puede registrar la recepción. Cargá una en
            Administración → Sucursales.
          </Alert>
        )}
      </Box>
    );
  }

  const persist = async (decision: ReceptionDecision) => {
    const input: ReceiveDeviceInput = {
      decision,
      // Optional when rejecting: the phone may never have reached a branch (BR-022, BR-032).
      branchId: branchId || undefined,
      phoneModelId,
      storageGb: Number(storageGb),
      serialNumber: serialNumber.trim() || undefined,
      colorId: colorId || undefined,
      // An emptied battery says it was not measured; omitting it would keep the declared one.
      batteryHealth: batteryHealth.trim() ? Number(batteryHealth) : null,
      paidPrice: paidPrice.trim() || undefined,
      commercialModality: commercialModality || undefined,
      // The condition travels with the reception: one form, one write. A phone is never left
      // received with its state pending on a second save somebody may not come back to do.
      answers: conditionAnswers(quotation.inspectionFields, condition),
      inspectionNotes: inspectionNotes.trim() || null,
    };

    setIsSaving(true);
    setError(null);

    try {
      onReceived(await receiveQuotationDevice(token, quotation.id, input));
      setPendingDifferences(null);
      // A rejected request goes back to waiting for a renegotiation; an approved one shows its
      // device sheet instead of this form either way.
      setInspectionNotes('');
      setIsReceiving(false);
    } catch (cause) {
      setPendingDifferences(null);
      setError(cause instanceof Error ? cause.message : 'No pudimos registrar la recepción.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDecision = async (decision: ReceptionDecision) => {
    if (!phoneModelId) {
      setError('Elegí el modelo del equipo.');
      return;
    }

    // Only approving creates a device, and a device has a branch and a modality (BR-022, BR-032).
    // A request can be rejected before the phone ever reaches PhoneTec, so rejecting asks for
    // neither.
    if (decision === 'APPROVE' && !branchId) {
      setError('Elegí en qué sucursal queda el equipo.');
      return;
    }

    if (decision === 'APPROVE' && !commercialModality) {
      setError('Elegí la modalidad comercial.');
      return;
    }

    const capacity = Number(storageGb);

    if (!Number.isInteger(capacity) || capacity <= 0) {
      setError('La capacidad tiene que ser un número entero mayor que cero.');
      return;
    }

    const battery = batteryHealth.trim() ? Number(batteryHealth) : undefined;

    if (battery !== undefined && (!Number.isInteger(battery) || battery < 0 || battery > 100)) {
      setError('La batería tiene que ser un número entero entre 0 y 100.');
      return;
    }

    const paidPriceProblem = paidPrice.trim() && priceError(paidPrice, 'El valor ajustado');

    if (paidPriceProblem) {
      setError(paidPriceProblem);
      return;
    }

    const differences = conditionDifferences(
      quotation.inspectionFields,
      quotation.answers,
      condition,
      batteryHealth.trim(),
    );

    if (differences.length) {
      setPendingDecision(decision);
      setPendingDifferences(differences);
      return;
    }

    await persist(decision);
  };

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        RECEPCIÓN DEL EQUIPO
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        {isRejected
          ? 'El formulario viene cargado con lo observado cuando se rechazó. Ajustá lo renegociado y aprobá: la cotización pasa a Aprobado y se crea el equipo.'
          : 'El formulario viene cargado con lo declarado. Revisalo contra el equipo que tenés en mano, corregí solo lo que difiere y completá número de serie, color y sucursal. Aprobar crea el equipo; rechazar guarda lo observado en la cotización para poder renegociarla.'}
      </Typography>
      <Grid container spacing={1.5}>
        {/*
          Starts at the declared model. A request from before the catalogue existed names none,
          and shows what was declared as a hint.
        */}
        <CatalogueModelFields
          options={modelOptions}
          phoneModelId={phoneModelId}
          emptyModelLabel={
            quotation.phoneModelId ? 'Elegí un modelo' : `Declarado: ${quotation.model}`
          }
          onChange={(nextModelId) => {
            setPhoneModelId(nextModelId);
            setColorId((current) => colorAfterModelChange(modelOptions, nextModelId, current));
          }}
        />
        <Grid item xs={12}>
          {/*
            Only the branches PhoneTec still operates: a closed one cannot be where the phone
            is standing, and the backend refuses it too.
          */}
          <TextField
            select
            fullWidth
            size="small"
            label="Sucursal"
            value={branchId}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            helperText="Dónde queda el equipo. Obligatoria para aprobar."
            onChange={(event) => setBranchId(event.target.value)}
          >
            <MenuItem value="">Elegí una sucursal</MenuItem>
            {activeBranches.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name} · {option.city}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Capacidad (GB)"
            value={storageGb}
            onChange={(event) => setStorageGb(event.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Batería (%)"
            value={batteryHealth}
            inputProps={{ min: 0, max: 100, step: 1 }}
            onChange={(event) => setBatteryHealth(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ColorField
            options={modelOptions}
            phoneModelId={phoneModelId}
            colorId={colorId}
            onChange={setColorId}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Modalidad comercial"
            helperText="Obligatoria para aprobar."
            value={commercialModality}
            onChange={(event) =>
              setCommercialModality(event.target.value as CommercialModality | '')
            }
          >
            <MenuItem value="">Elegí una modalidad</MenuItem>
            <MenuItem value="DIRECT_PURCHASE">Compra directa</MenuItem>
            <MenuItem value="TRADE_IN">Parte de pago</MenuItem>
            <MenuItem value="CONSIGNMENT">Consignación</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Número de serie"
            value={serialNumber}
            onChange={(event) => setSerialNumber(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Valor ajustado (USD)"
            value={paidPrice}
            placeholder="270"
            inputProps={{ inputMode: 'decimal' }}
            helperText="Lo que paga PhoneTec. En consignación, lo que se le paga al dueño al vender."
            onChange={(event) => setPaidPrice(event.target.value)}
          />
        </Grid>
      </Grid>

      {/*
        The state of the phone, in the same form: the person has it in hand now. Each question
        starts at the declared answer, which stays shown underneath once it is changed.
      */}
      <Typography variant="overline" color="text.secondary" component="p" sx={{ mt: 2 }}>
        ESTADO TÉCNICO Y ESTÉTICO
      </Typography>
      {conditionQuestions(quotation.inspectionFields).length ? null : (
        <Typography variant="caption" color="text.secondary">
          Esta solicitud no declaró campos de estado, así que solo se registran observaciones.
        </Typography>
      )}
      <ConditionFields
        fields={quotation.inspectionFields}
        declared={quotation.answers}
        draft={condition}
        notes={inspectionNotes}
        onChange={(fieldId, value) => setCondition((current) => ({ ...current, [fieldId]: value }))}
        onNotesChange={setInspectionNotes}
      />

      {error ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      ) : null}
      {/*
        Two explicit decisions close the reception (ADR 0005 point c). A rejected request is
        only approved: Rejected goes nowhere else (BR-017). The observations above are the
        reason when rejecting, and stay as a note on the request (BR-020).
      */}
      <Stack direction="row" spacing={1} mt={1.5}>
        <PrimaryButton onClick={() => handleDecision('APPROVE')} disabled={isSaving}>
          {isSaving ? 'Guardando...' : 'Aprobar'}
        </PrimaryButton>
        {isRejected ? null : (
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleDecision('REJECT')}
            disabled={isSaving}
          >
            Rechazar
          </Button>
        )}
        <Button color="inherit" onClick={resetForm} disabled={isSaving}>
          Cancelar
        </Button>
      </Stack>

      <ConditionConfirmDialog
        differences={pendingDifferences}
        isSaving={isSaving}
        onCancel={() => setPendingDifferences(null)}
        onConfirm={() => persist(pendingDecision)}
      />
    </Box>
  );
}
